'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { MARKETING } from 'self-service-api/constants/ProductLines';
import { CONTACT, USER } from 'self-service-api/constants/UnitTypes';
import styled from 'styled-components';
import { hasPricingAndPurchasingSucceededSelector, hasPurchaseMotionsFailedSelector, isMerchCardCTAsDataLoadingSelector } from '../../selectors/LoadingStateSelectors';
import { hasProductVersionInProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, productSelector, quantityPackForProductVersionSelector } from '../../selectors/ProductsSelectors';
import { shouldShowTermSelectorForMerchSelector } from '../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../utils/stateUtils';
import CurrentPlanTag from '../common/CurrentPlanTag';
import DetailedTermSelector from '../common/DetailedTermSelector';
import SkeletonLoadWrapper from '../loading/SkeletonLoadWrapper';
import AdditionalUnitsPicker from '../plan-builder/AdditionalUnitsPicker';
import UIImage from 'UIComponents/image/UIImage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import MerchCardCTAsContainer from './MerchCardCTAsContainer';
import { isMon569VariantSelector } from '../../selectors/ExperimentTreatmentSelectors';
import H3 from 'UIComponents/elements/headings/H3';
import { HighlightedProductsHeaderConfig } from '../../constants/HighlightedProductsHeaderConfig';
import { Section } from '../common/MerchCardSharedComponents';
import { MERCH_CARD } from '../../constants/Tracking';
import ViewAllFeatures from './ViewAllFeatures';
import HighlightedMerchPriceDisplay from './HighlightedMerchPriceDisplay';
import { getGAClassName } from 'pricing-pages-lib/utils/publicPageUrls';
import ProductLineToGaMap from '../../constants/ProductLineToGaMap';
import FallbackMerchCardCTAsContainer from './FallbackMerchCardCTAsContainer';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import BusinessObjectivesSSAPI from './business-objectives/BusinessObjectivesSSAPI';
const StyledTermSelectorContainer = styled(DetailedTermSelector).withConfig({
  displayName: "HighlightedMerchCard__StyledTermSelectorContainer",
  componentId: "sc-1sfmr5z-0"
})(["max-width:300px;margin:0 auto;"]);

const HighlightedMerchCard = ({
  productLevel,
  productLine,
  commonStyles,
  productLevelsToShow
}) => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isMerchCardCTAsDataLoading = useSelector(isMerchCardCTAsDataLoadingSelector);
  const hasPurchaseMotionsFailed = useSelector(hasPurchaseMotionsFailedSelector);
  const skuId = useSelector(state => currentSkuIdForProductSelector(state, {
    productLine,
    productLevel
  }));
  const product = useSelector(state => productSelector(state, {
    productLine,
    productLevel
  }));
  const apiName = product && product.apiName;
  const seleniumId = apiName || 'loading';
  const quantityPackSkuId = useSelector(state => {
    if (!skuId) return null;
    const quantityPack = quantityPackForProductVersionSelector(state, {
      productVersionSkuId: skuId,
      unitType: productLine === MARKETING ? CONTACT : USER
    });
    return quantityPack && quantityPack.skuId;
  });
  const [quantityPackQuantity, setQuantityPackQuantity] = useState(0);
  const ownsProductVersion = useSelector(state => hasPricingAndPurchasingSucceeded && hasProductVersionInProductInventorySelector(state, {
    skuId
  }));
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    title,
    icon
  } = HighlightedProductsHeaderConfig[productLine][productLevel];
  const shouldShowTermSelector = useSelector(state => skuId && shouldShowTermSelectorForMerchSelector(state, {
    skuId
  }));
  const isMon569Variant = useSelector(isMon569VariantSelector);
  const showBorderBottom = productLevel && productLevelsToShow && productLevelsToShow.indexOf(productLevel) < productLevelsToShow.length - 1; // In order for the grid to work, we need this to remain enclosed in a Fragment and not a div.
  // Children of the display grid item must get the grid-start attributes directly

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Section, {
      isMon569Variant: isMon569Variant,
      className: "flex flex-column align-center justify-center p-top-6",
      style: Object.assign({}, commonStyles, {
        msGridRow: 1,
        gridRowStart: 1,
        position: 'relative'
      }),
      "data-selenium-id": `${seleniumId}-merch-card`,
      children: [ownsProductVersion && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsxs("div", {
        className: "display-flex align-center",
        children: [/*#__PURE__*/_jsx(UIImage, {
          className: "m-right-3",
          src: icon
        }), /*#__PURE__*/_jsx(H3, {
          className: "m-bottom-0",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: title
          })
        })]
      })]
    }), /*#__PURE__*/_jsx(Section, {
      isMon569Variant: isMon569Variant,
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        msGridRow: 2,
        gridRowStart: 2
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        className: "flex-column align-center",
        Content: HighlightedMerchPriceDisplay,
        contentProps: {
          queryParamTerm,
          skuId,
          productLine,
          style: {
            textAlign: 'center',
            marginTop: 0
          },
          useLargePrice: true,
          quantityPackSkuId,
          quantityPackQuantity
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      isMon569Variant: isMon569Variant,
      className: "p-top-4 text-center",
      style: Object.assign({}, commonStyles, {
        msGridRow: 3,
        gridRowStart: 3
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        className: "flex-column align-center",
        heights: [34, 76],
        widths: ['226px', '320px'],
        Content: AdditionalUnitsPicker,
        contentProps: {
          quantityPackSkuId,
          quantityPackQuantity,
          setQuantityPackQuantity,
          productConfigurations: [createProductConfig({
            skuId,
            quantity: 1
          })],
          compact: true,
          queryParamTerm,
          trackingSource: MERCH_CARD
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      isMon569Variant: isMon569Variant,
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        msGridRow: 4,
        gridRowStart: 4
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [41],
        widths: [300],
        className: "flex-column align-center",
        Content: shouldShowTermSelector ? StyledTermSelectorContainer : null,
        contentProps: {
          skuIds: quantityPackQuantity > 0 ? [skuId, quantityPackSkuId] : [skuId],
          onChange: setQueryParamTerm,
          activeTerm: queryParamTerm,
          trackingSource: MERCH_CARD
        }
      })
    }), /*#__PURE__*/_jsx(Section, {
      isMon569Variant: isMon569Variant,
      className: "p-top-4",
      style: Object.assign({}, commonStyles, {
        msGridRow: 5,
        gridRowStart: 5
      }),
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: isMerchCardCTAsDataLoading,
        isError: hasPurchaseMotionsFailed,
        heights: [47],
        Content: MerchCardCTAsContainer,
        contentProps: {
          queryParamTerm,
          skuId,
          productLine,
          additionalUnitQuantity: quantityPackQuantity,
          ctaStyles: {
            width: '100%',
            lineHeight: '23px'
          }
        },
        ErrorContent: FallbackMerchCardCTAsContainer,
        errorContentProps: {
          skuId
        }
      })
    }), /*#__PURE__*/_jsx(BusinessObjectivesSSAPI, {
      productLine: productLine,
      productLevel: productLevel,
      commonStyles: commonStyles
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-3 p-x-0",
      isMon569Variant: isMon569Variant,
      style: Object.assign({}, commonStyles, {
        msGridRow: 8,
        gridRowStart: 8
      }),
      showBorderBottom: showBorderBottom,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [50],
        style: {
          width: '100%'
        },
        children: /*#__PURE__*/_jsx(ViewAllFeatures, {
          productLine: productLine,
          productLevel: productLevel,
          gaClassName: getGAClassName(`${ProductLineToGaMap[productLine]}features`),
          trackingSource: MERCH_CARD
        })
      })
    })]
  });
};

export default HighlightedMerchCard;